import { Link } from '@tanstack/react-router';
import {
  DashboardDataTableColumnKind,
  type DashboardDataTableColumnDefinition,
} from '@shigo-ui/dashboard-ui';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shigo-ui/ui';

import { formatDisplayDate, numberFormat } from '../../../../lib/format';
import { dealAuctionTypeLabel, dealStatusLabel, dealStatusTone, type Deal } from '../model';

export function createDealStatusColumn(): DashboardDataTableColumnDefinition<Deal> {
  return {
    id: 'status',
    kind: DashboardDataTableColumnKind.Status,
    header: 'Status',
    headerClassName: 'w-32',
    className: 'w-32',
    label: (deal) => dealStatusLabel(deal.status),
    tone: (deal) => dealStatusTone(deal.status),
  };
}

export function createDealIdColumn(): DashboardDataTableColumnDefinition<Deal> {
  return textColumn('dealId', 'Deal ID', (deal) => deal.dealId || '-');
}

export function createDealAuctionTypeColumn(): DashboardDataTableColumnDefinition<Deal> {
  return textColumn('auctionType', 'Auction type', (deal) =>
    dealAuctionTypeLabel(deal.auctionType),
  );
}

export function createDealFlightDatesColumn(): DashboardDataTableColumnDefinition<Deal> {
  return textColumn('flightDates', 'Flight dates', (deal) =>
    formatDealFlightDates(deal.startDate, deal.endDate),
  );
}

export function createDealBidFloorColumn(): DashboardDataTableColumnDefinition<Deal> {
  return textColumn('bidFloor', 'Bid floor', (deal) =>
    deal.bidFloor === undefined ? '-' : formatCurrency(deal.bidFloor, deal.bidFloorCur),
  );
}

export function createDealIntegrationsColumn(): DashboardDataTableColumnDefinition<Deal> {
  return {
    id: 'integrations',
    kind: DashboardDataTableColumnKind.Custom,
    header: 'DP integrations',
    headerClassName: 'w-36 text-center',
    className: 'w-36 text-center tabular-nums',
    cell: (deal) => {
      if (!deal.demandIntegration.length) return '0';
      return (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              className="text-primary h-8 min-w-8 px-2 font-medium"
              aria-label={`Show ${String(deal.demandIntegration.length)} integrations for ${deal.name}`}
            >
              {deal.demandIntegration.length}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" className="min-w-64">
            {deal.demandIntegration.map((integration) => {
              const demandPartnerId = integration.demandPartner?.id;
              if (!demandPartnerId) {
                return (
                  <DropdownMenuItem key={integration.id} disabled>
                    {integration.name}
                  </DropdownMenuItem>
                );
              }
              return (
                <DropdownMenuItem key={integration.id} asChild>
                  <Link
                    to="/demand/integrations/$integrationId/demand/$demandPartnerId/details/overview"
                    params={{ integrationId: integration.id, demandPartnerId }}
                    className="text-primary cursor-pointer py-3 font-medium"
                  >
                    {integration.name}
                  </Link>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  };
}

export function createDealUpdatedColumn(): DashboardDataTableColumnDefinition<Deal> {
  return textColumn('updatedOn', 'Updated', (deal) => formatDate(deal.updatedOn), 'w-40');
}

export function formatDealFlightDates(start?: string, end?: string) {
  if (!start && !end) return '-';
  return `${formatDate(start)} – ${formatDate(end)}`;
}

function textColumn(
  id: string,
  header: string,
  value: (deal: Deal) => React.ReactNode,
  className = 'w-48',
): DashboardDataTableColumnDefinition<Deal> {
  return {
    id,
    kind: DashboardDataTableColumnKind.Text,
    header,
    headerClassName: className,
    className,
    value,
  };
}

function formatDate(value?: string) {
  return formatDisplayDate(value, { fallback: 'Open', keepInvalid: true, dateStyle: 'medium' });
}

function formatCurrency(value: number, currency: string) {
  try {
    return numberFormat({ style: 'currency', currency, minimumFractionDigits: 2 }).format(value);
  } catch {
    return `${currency} ${String(value)}`;
  }
}
